import { FC, useMemo } from 'react';

import { startOfDay } from 'date-fns';
import { getDateFromString, getMaxDate } from 'utils/date';

import auctionCampaignPromotionDatesButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_promotion_dates_button_click';

import useTrls from 'hooks/translation/useTrls';

import { useAuctionDateValidation } from 'components/AuctionBuilder/AuctionPeriod/useAuctionDateValidation';
import { DateSelect } from 'components/common/DateSelect/DateSelect';

import {
    AuctionBuilderDatesErrorCode,
    AuctionDataField,
    auctionSelector,
    setEndDate,
    setIsDatesChanged,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    datePlaceholder: 'AuctionBuilder.CampaignPeriod.AuctionDateEnd.placeholder',
    error: {
        [AuctionBuilderDatesErrorCode.ValueMissingForStrategy]:
            'AuctionBuilder.CampaignPeriod.error.valueMissingForStrategy',
    },
};

const TODAY = startOfDay(new Date());

export const AuctionDateEnd: FC = () => {
    const startDate = useSelector(auctionSelector.startDate);
    const endDate = useSelector(auctionSelector.endDate);
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const campaignId = useSelector(auctionSelector.auctionCampaign)?.campaignId;
    const campaignExpirationDate = useSelector((state) => state.user.user?.currentAccount.expirationDate);
    const errorCode = useSelector(auctionSelector.errors.endAt);

    const selectedDate = getDateFromString(endDate);

    const minDate = useMemo(() => {
        const dates = [getDateFromString(startDate), TODAY].filter((date) => !!date) as Date[];
        return getMaxDate(dates);
    }, [startDate]);

    const maxDate = campaignExpirationDate ? getDateFromString(campaignExpirationDate) : undefined;

    const dispatch = useDispatch();
    const trls = useTrls();
    const validateDate = useAuctionDateValidation();

    const onDateSelect = (date: string) => {
        dispatch([setEndDate(date), setIsDatesChanged(true)]);

        validateDate(date, AuctionDataField.EndAt);

        auctionCampaignPromotionDatesButtonClick({
            vacancyId,
            campaignId,
            startAt: startDate,
            endAt: date,
        });
    };

    return (
        <DateSelect
            data-qa="auction-date-end-input"
            maxDate={maxDate}
            minDate={minDate}
            onDateSelect={onDateSelect}
            placeholder={trls(TrlKeys.datePlaceholder)}
            selectedDate={selectedDate || undefined}
            inputInvalid={!!errorCode}
            errorMessage={
                errorCode ? trls(TrlKeys.error[AuctionBuilderDatesErrorCode.ValueMissingForStrategy]) : undefined
            }
        />
    );
};
